import type { VoiceToolMedia } from "@/types/voice-chat"
import type {
  ToolOutputDelivery,
  VoiceRealtimeClientEvent,
  VoiceRealtimeEvent,
} from "@/types/voice-chat-rtc"

import { errorMessage } from "@/lib/exception-utils"
import type { ResponseGate } from "@/lib/voice-chat/response-gate"
import {
  voiceFunctionCallClientEvent,
  voiceFunctionCallOutputClientEvent,
  voiceToolMediaClientEvent,
  voiceToolOutputClientEvent,
} from "@/lib/voice-chat/voice-chat-utils"
import type { VoiceTracer } from "@/lib/voice-trace/voice-trace"

const TOOL_RESULT_ACKNOWLEDGEMENT_DEADLINE_MS = 10_000

type DeliveryStage =
  | "fetching_media"
  | "awaiting_publication"
  | "awaiting_acknowledgement"
  | "reconciling"
  | "ready_retry"

// One conversation item the delivery must land, in sequence order. `kind` only
// decides what happens when OpenAI rejects the item; sending, acknowledging,
// and reconciling are identical for all of them.
interface SequenceItem {
  itemId: string
  kind: "snapshot" | "media" | "output"
  eventIdPrefix: string
  buildEvent(eventId: string, previousItemId?: string): VoiceRealtimeClientEvent
}

interface PendingToolResultDelivery {
  callId: string
  contextOnly: boolean
  outputDelivery: ToolOutputDelivery
  items: SequenceItem[]
  cursor: number
  outputItemId: string
  mediaItemId: string
  eventId: string | null
  media: VoiceToolMedia[] | null
  mediaRejected: boolean
  stage: DeliveryStage
  acknowledgementDeadline: number | null
  resolve: ((accepted: boolean) => void) | null
}

interface ToolResultRelayDeps {
  fetchToolMedia(args: {
    openaiCallId: string
    callId: string
  }): Promise<VoiceToolMedia[]>
  isContextOnlyTool(toolName: string): boolean
  hasOtherToolWorkOutstanding(callId: string): boolean
  send(event: VoiceRealtimeClientEvent): boolean
  trace: VoiceTracer
  gate: ResponseGate
}

export class ToolResultRelay {
  private readonly deliveries = new Map<string, PendingToolResultDelivery>()
  private readonly itemCallIds = new Map<string, string>()
  private readonly eventCallIds = new Map<string, string>()
  private sequence = 0
  private closed = false

  constructor(private deps: ToolResultRelayDeps) {}

  deliver(delivery: ToolOutputDelivery, openaiCallId: string | null) {
    if (this.closed) return Promise.resolve(false)

    const existing = this.deliveries.get(delivery.callId)
    if (existing) return this.retry(existing, delivery)

    const pending = this.newDelivery(delivery)
    this.deliveries.set(delivery.callId, pending)
    if (
      !("images" in delivery.result) ||
      delivery.result.images === undefined
    ) {
      pending.stage = "awaiting_publication"
      const accepted = this.waitForDelivery(pending)
      this.registerReady(pending)
      return accepted
    }

    if (!openaiCallId) {
      this.deliveries.delete(pending.callId)
      return Promise.resolve(false)
    }

    const accepted = this.waitForDelivery(pending)
    void this.fetchAndPrepareMedia(pending, openaiCallId)
    return accepted
  }

  acknowledgeItem(itemId: string) {
    const callId = this.itemCallIds.get(itemId)
    if (!callId) return false

    const pending = this.deliveries.get(callId)
    if (!pending) return true
    if (itemId !== this.currentItem(pending).itemId) return true
    if (
      pending.stage !== "awaiting_acknowledgement" &&
      pending.stage !== "reconciling"
    ) {
      return true
    }

    this.clearAcknowledgementDeadline(pending)
    if (pending.cursor === pending.items.length - 1) {
      this.deps.gate.toolResultItemAcknowledged(pending.outputItemId)
      this.finish(pending, true)
      return true
    }

    pending.cursor += 1
    this.sendCurrentItem(pending)
    return true
  }

  handleError(event: Extract<VoiceRealtimeEvent, { type: "error" }>) {
    const error = event.error
    if (!error?.event_id) return false

    const callId = this.eventCallIds.get(error.event_id)
    if (!callId) return false

    const pending = this.deliveries.get(callId)
    if (!pending) return true
    if (error.event_id !== pending.eventId) return true

    if (pending.stage === "reconciling") {
      if (this.itemMissing(event)) {
        this.clearAcknowledgementDeadline(pending)
        this.sendCurrentItem(pending)
      }
      return true
    }
    if (pending.stage !== "awaiting_acknowledgement") return true

    this.clearAcknowledgementDeadline(pending)
    const rejected = this.currentItem(pending)
    if (rejected.kind === "output") {
      if (!pending.contextOnly) {
        this.deps.gate.toolResultItemRejected(pending.outputItemId)
      }
      pending.stage = "awaiting_publication"
      this.release(pending, false)
      return true
    }

    if (rejected.kind === "media") {
      pending.mediaRejected = true
      pending.outputDelivery = {
        callId: pending.callId,
        toolName: pending.outputDelivery.toolName,
        customerSpeechGeneration:
          pending.outputDelivery.customerSpeechGeneration,
        result: { isError: true, error: "media_rejected" },
      }
      pending.items.splice(pending.cursor, 1)
    } else {
      this.deps.trace.warn("voice_tool_snapshot_rejected", {
        callId: pending.callId,
        itemId: rejected.itemId,
        code: error.code,
      })
      this.dropSnapshotItems(pending)
    }
    this.sendCurrentItem(pending)
    return true
  }

  close() {
    this.closed = true
    this.deliveries.forEach((pending) => this.finish(pending, false))
  }

  private newDelivery(delivery: ToolOutputDelivery): PendingToolResultDelivery {
    this.sequence += 1
    const outputItemId = `item_mindtrip_tool_output_${this.sequence}`
    this.sequence += 1
    const mediaItemId = `item_mindtrip_tool_media_${this.sequence}`
    const pending: PendingToolResultDelivery = {
      callId: delivery.callId,
      // A context-only output is only context when some other result will carry
      // the answer; when it is the sole outstanding work it must own a response
      // or the turn never completes.
      contextOnly:
        this.deps.isContextOnlyTool(delivery.toolName) &&
        this.deps.hasOtherToolWorkOutstanding(delivery.callId),
      outputDelivery: delivery,
      items: [],
      cursor: 0,
      outputItemId,
      mediaItemId,
      eventId: null,
      media: null,
      mediaRejected: false,
      stage: "fetching_media",
      acknowledgementDeadline: null,
      resolve: null,
    }

    delivery.additionalToolCalls?.forEach((call) => {
      this.sequence += 1
      const callItemId = `item_mindtrip_snap_call_${this.sequence}`
      this.sequence += 1
      const outputItemId = `item_mindtrip_snap_output_${this.sequence}`
      // OpenAI caps realtime item ids at 32 characters, so keep these short.
      pending.items.push({
        itemId: callItemId,
        kind: "snapshot",
        eventIdPrefix: "voice_tool_snapshot_event",
        buildEvent: (eventId, previousItemId) =>
          voiceFunctionCallClientEvent(
            eventId,
            callItemId,
            call,
            previousItemId
          ),
      })
      pending.items.push({
        itemId: outputItemId,
        kind: "snapshot",
        eventIdPrefix: "voice_tool_snapshot_event",
        buildEvent: (eventId, previousItemId) =>
          voiceFunctionCallOutputClientEvent(
            eventId,
            outputItemId,
            call.callId,
            call.output,
            previousItemId
          ),
      })
    })
    pending.items.push({
      itemId: outputItemId,
      kind: "output",
      eventIdPrefix: "voice_tool_output_event",
      buildEvent: (eventId, previousItemId) =>
        voiceToolOutputClientEvent(
          outputItemId,
          pending.outputDelivery,
          eventId,
          previousItemId
        ),
    })
    return pending
  }

  private retry(
    pending: PendingToolResultDelivery,
    delivery: ToolOutputDelivery
  ) {
    if (pending.resolve) return Promise.resolve(false)
    if (!pending.mediaRejected) pending.outputDelivery = delivery

    const accepted = this.waitForDelivery(pending)
    if (pending.stage === "awaiting_publication") {
      this.registerReady(pending)
    }
    if (pending.stage === "ready_retry") {
      this.sendCurrentItem(pending)
    }
    return accepted
  }

  private async fetchAndPrepareMedia(
    pending: PendingToolResultDelivery,
    openaiCallId: string
  ) {
    try {
      const media = await this.deps.fetchToolMedia({
        openaiCallId,
        callId: pending.callId,
      })
      if (this.closed) {
        this.finish(pending, false)
        return
      }

      pending.media = media
      pending.items.splice(pending.items.length - 1, 0, {
        itemId: pending.mediaItemId,
        kind: "media",
        eventIdPrefix: "voice_tool_media_event",
        buildEvent: (eventId, previousItemId) =>
          voiceToolMediaClientEvent(
            eventId,
            pending.mediaItemId,
            media,
            previousItemId
          ),
      })
      pending.stage = "awaiting_publication"
      this.registerReady(pending)
    } catch (error) {
      this.deps.trace.warn("voice_tool_result_fetch_failed", {
        callId: pending.callId,
        message: errorMessage(error, "Could not resolve tool media."),
      })
      if (this.closed) {
        this.finish(pending, false)
        return
      }

      pending.outputDelivery = {
        callId: pending.callId,
        toolName: pending.outputDelivery.toolName,
        customerSpeechGeneration:
          pending.outputDelivery.customerSpeechGeneration,
        result: { isError: true, error: "media_unavailable" },
      }
      pending.stage = "awaiting_publication"
      this.registerReady(pending)
    }
  }

  private registerReady(pending: PendingToolResultDelivery) {
    if (pending.contextOnly) {
      this.sendCurrentItem(pending)
      return
    }

    this.deps.gate.toolResultReady({
      itemId: pending.outputItemId,
      callId: pending.callId,
      customerSpeechGeneration: pending.outputDelivery.customerSpeechGeneration,
      publish: () => this.publish(pending),
    })
  }

  private publish(pending: PendingToolResultDelivery) {
    if (this.closed) {
      return false
    }

    const accepted = this.sendCurrentItem(pending)
    if (!accepted) pending.stage = "awaiting_publication"
    return accepted
  }

  private sendCurrentItem(pending: PendingToolResultDelivery) {
    const item = this.currentItem(pending)
    pending.stage = "awaiting_acknowledgement"
    this.sequence += 1
    const eventId = `${item.eventIdPrefix}_${this.sequence}`
    pending.eventId = eventId
    this.itemCallIds.set(item.itemId, pending.callId)
    this.eventCallIds.set(eventId, pending.callId)
    const previousItem =
      pending.cursor > 0 ? pending.items[pending.cursor - 1] : null
    if (!this.deps.send(item.buildEvent(eventId, previousItem?.itemId))) {
      this.eventCallIds.delete(eventId)
      pending.stage = "ready_retry"
      this.release(pending, false)
      return false
    }
    if (item.kind === "media" && pending.media) {
      this.deps.trace.info(
        "voice_tool_media_sent",
        {
          callId: pending.callId,
          mediaItemId: item.itemId,
          mediaEventId: eventId,
          mediaCount: pending.media.length,
          mimeTypes: pending.media.map(({ mimeType }) => mimeType),
          backendByteCounts: pending.media.map(({ byteCount }) => byteCount),
        },
        "out"
      )
    }
    this.armAcknowledgementDeadline(pending)
    return true
  }

  // A rejected snapshot item takes the rest of the snapshot with it: the skill
  // still loads, the model just lacks the trip this turn.
  private dropSnapshotItems(pending: PendingToolResultDelivery) {
    let count = 0
    while (pending.items[pending.cursor + count].kind === "snapshot") count += 1
    pending.items.splice(pending.cursor, count)
  }

  private sendRetrieve(pending: PendingToolResultDelivery) {
    this.sequence += 1
    const eventId = `voice_tool_item_retrieve_${this.sequence}`
    pending.eventId = eventId
    pending.stage = "reconciling"
    this.eventCallIds.set(eventId, pending.callId)
    if (
      !this.deps.send({
        type: "conversation.item.retrieve",
        event_id: eventId,
        item_id: this.currentItem(pending).itemId,
      })
    ) {
      this.eventCallIds.delete(eventId)
      if (!pending.contextOnly) {
        this.deps.gate.toolResultItemRejected(pending.outputItemId)
      }
      this.finish(pending, false)
      return
    }
    this.armAcknowledgementDeadline(pending)
  }

  private armAcknowledgementDeadline(pending: PendingToolResultDelivery) {
    this.clearAcknowledgementDeadline(pending)
    pending.acknowledgementDeadline = window.setTimeout(() => {
      pending.acknowledgementDeadline = null
      this.sendRetrieve(pending)
    }, TOOL_RESULT_ACKNOWLEDGEMENT_DEADLINE_MS)
  }

  private clearAcknowledgementDeadline(pending: PendingToolResultDelivery) {
    if (pending.acknowledgementDeadline === null) return
    window.clearTimeout(pending.acknowledgementDeadline)
    pending.acknowledgementDeadline = null
  }

  private currentItem(pending: PendingToolResultDelivery) {
    return pending.items[pending.cursor]
  }

  private itemMissing(event: Extract<VoiceRealtimeEvent, { type: "error" }>) {
    return (
      event.error?.type === "invalid_request_error" &&
      event.error.code === "item_retrieve_invalid_item_id" &&
      event.error.param === null
    )
  }

  private waitForDelivery(pending: PendingToolResultDelivery) {
    return new Promise<boolean>((resolve) => {
      pending.resolve = resolve
    })
  }

  private release(pending: PendingToolResultDelivery, accepted: boolean) {
    const resolve = pending.resolve
    pending.resolve = null
    resolve?.(accepted)
  }

  private finish(pending: PendingToolResultDelivery, accepted: boolean) {
    this.clearAcknowledgementDeadline(pending)
    this.itemCallIds.forEach((callId, itemId) => {
      if (callId === pending.callId) this.itemCallIds.delete(itemId)
    })
    this.eventCallIds.forEach((callId, eventId) => {
      if (callId === pending.callId) this.eventCallIds.delete(eventId)
    })
    this.deliveries.delete(pending.callId)
    this.release(pending, accepted)
  }
}
